import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { NavLink, Route } from 'react-router-dom';
import { LoadingSpinner, styles } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';
import { chatamsBlue } from '../../styles/theme';
import { Team, useCachedOktaQuery } from '../../data';
import PlayerSelectorCards from '../../components/PlayerSelectorCards';
import { store, actions } from '../../store';
import { StyledNavCard, hoverColor } from '../../components/NavCard';
import usePlayerSelector from '../../utils/usePlayerSelector';
import TeamSeriesHeader from './TeamSeriesHeader';
import { StealingAgainstReport, CatcherWristband, TeamPlaylists, TeamKeyTakeaways, DugoutTeamKeyTakeaways } from './Team';
import { OpposingPitcherReport, OpposingBatterReport } from './Player';
import ComponentErrorFallback from '../../components/ComponentErrorFallback';
import useOpposingTeamSelector from '../../utils/useOpposingTeamSelector';
import MatchupReportBase from './Player/MatchupReportBase';
import TippingReport from './Player/TippingReport';
import TippingTeamReport from './Team/Tipping/TippingReport';
import PitcherOverview from './Player/PitcherOverview';
import GamePrep from './Player/GamePrep';
import Approach from './Player/Approach';
import PlayerVideoReviewReport from './Player/PlayerVideoReviewReport';
import OpposingPlayerPlaylists from './Player/OpposingPlayerPlaylists';
import KeyTakeaways from './Player/KeyTakeaways';
import BaseInfieldReport from './Player/BaseInfieldReport';
import GameLogs from './Player/GameLogs';

const StyledInGameReport = styled.div`
    margin: -${styles.theme.margin.body};
    height: calc(100% + 2 * ${styles.theme.margin.body});
    display: flex;
    flex-direction: column;
    position: relative;
`;
const StyledInGameReportSelector = styled.div`
    flex-grow: 0;
    width: 225px;
    min-width: 225px;
    border-right: 1px solid ${styles.theme.border.alto};
    background-color: white;
    p, a.h5 {
        background-color: white;
        padding: 15px 10px 7.5px;
        margin: 0;
    }
    a.h5 {
        display: block;
        padding-top: 7.5px;
        padding-left: 15px;
        color: ${chatamsBlue};
        &:hover {
            text-decoration: none;
            background-color: ${hoverColor};
        }
    }
    a.active {
        &.h5, &.h5:hover, ${StyledNavCard} .card-body {
            background-color: #CCE5FF;
        }
    }
    p {
        color: ${styles.theme.fontColors.doveGray};
        text-transform: uppercase;
        font-size: 1.15em;
    }
    ${StyledNavCard} {
        margin-bottom: 0;
        box-shadow: none;
        border-radius: 0;
        border: none;
        .card-title.h5 {
            font-size: 1.35em;
        }
        .card-body {
            padding: 10px;
        }
        img {
            width: 50px;
        }
    }
    p, a.h5, ${StyledNavCard} {
        border-bottom: 1px solid ${styles.theme.border.alto};
    }
`;
const StyledInGameReportContent = styled.div`
    flex-grow: 1;
    padding: ${styles.theme.margin.body};
`;
const StyledInGameReportContainer = styled.div`
    flex-grow: 1;
    display: flex;
    overflow-y: hidden;
    > ${StyledInGameReportSelector}, > ${StyledInGameReportContent} {
        overflow-y: auto;
    }
    > ${StyledInGameReportSelector} {
        overflow-x: hidden;
    }
`;
const StyledLandingPage = styled.div`
    h4 + h4 {
        margin-top: ${({ $numReports }) => 20 + ($numReports * 32)}px;
    }
`;

const playerSelectorCardsResponsive = { sm: 12, md: 12, lg: 12, xl: 12, height: 50 };
const Opponent = ({ match: { url, path, params: { teamId } } }) => {
    const { t: translate } = useTranslation();
    const { dispatch } = useContext(store);

    // eslint-disable-next-line no-param-reassign
    teamId = parseInt(teamId); // forces query keys below to match ones prefetched on the home page

    const onSetOpponentTeam = async (data) => {
        dispatch({ type: actions.SET_OPPONENT, data });
        dispatch({ type: actions.SET_MATCHUP_SELECTED_PITCHER, data: null });
    };

    const { IsPitcher, IsCatcher, Bats } = usePlayerSelector();

    const { data: team, refetch } = useCachedOktaQuery(['team', teamId], (accessToken) => Team.get(accessToken, teamId),
        { onSuccess: (data) => onSetOpponentTeam(data) });

    const opposingTeam = useOpposingTeamSelector();
    // eslint-disable-next-line eqeqeq
    if (opposingTeam?.TeamID != teamId) {
        refetch(); // this will re-load the opponent into state memory / useOpposingTeamSelector
    }

    const match = { params: { teamId, levelId: team?.LevelID } };
    let reports = [];

    if (window.$IsDugoutApp) {
        reports = [
            (IsPitcher || IsCatcher) && {
                title: translate('sections.opponent.catcher-wristband'),
                route: 'wristband-dugout',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <CatcherWristband {...props} match={match} />
                    </ErrorBoundary>
                ),
            },
            !IsPitcher && {
                title: translate('sections.opponent.stealing-against-report'),
                route: 'stealing-against',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <StealingAgainstReport {...props} />
                    </ErrorBoundary>
                ),
            },
            (IsPitcher || IsCatcher) && {
                title: translate('sections.opponent.all-key-takeaways'),
                route: 'keys',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <DugoutTeamKeyTakeaways {...props} match={match} />
                    </ErrorBoundary>
                ),
            },
            !IsPitcher && {
                title: translate('sections.opponent.tipping-report'),
                route: 'tipping',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <TippingTeamReport {...props} />
                    </ErrorBoundary>
                ),
            },
        ].filter((x) => x);
    } else {
        reports = [
            (IsPitcher || IsCatcher) && {
                title: translate('sections.opponent.catcher-wristband'),
                route: 'wristband-dugout',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <CatcherWristband {...props} match={match} />
                    </ErrorBoundary>
                ),
            },
            !IsPitcher && {
                title: translate('sections.opponent.stealing-against-report'),
                route: 'stealing-against',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <StealingAgainstReport {...props} />
                    </ErrorBoundary>
                ),
            },
            !window.$HideVideoComponents && {
                title: translate('sections.opponent.team-playlists'),
                route: 'team-playlists',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <TeamPlaylists {...props} match={match} />
                    </ErrorBoundary>
                ),
            },
            (IsPitcher || IsCatcher) && {
                title: translate('sections.opponent.all-key-takeaways'),
                route: 'keys',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <TeamKeyTakeaways {...props} match={match} />
                    </ErrorBoundary>
                ),
            },
            !IsPitcher && {
                title: translate('sections.opponent.tipping-report'),
                route: 'tipping',
                render: (props) => (
                    <ErrorBoundary FallbackComponent={ComponentErrorFallback}>
                        <TippingTeamReport {...props} />
                    </ErrorBoundary>
                ),
            },
            // { title: 'Oppenent Leads Report', route: 'opponent-leads', component: OpponentLeadsReport },
            // { title: 'Throwing Arms/Run Times', route: 'run-times', component: ThrowingArmsRunTimesReport },
        ].filter((x) => x);
    }

    const videoReviewTab = {
        title: translate('sections.opponent.video-review'),
        route: 'video',
        component: ({ isLoading, player }) => (
            <PlayerVideoReviewReport isPlayerLoading={isLoading} player={player} />
        ),
    };
    const opposingPlayerPlaylists = {
        title: translate('sections.opponent.playlists'),
        route: 'playlists',
        component: ({ isLoading, player, playerId }) => (
            <OpposingPlayerPlaylists isPlayerLoading={isLoading} player={player} playerId={playerId} />
        ),
    };

    const playerTabs = [
        ...(window.$OpposingPlayerPlaylistsDisabled ? [] : [opposingPlayerPlaylists]),
        ...(window.$MatchupVideoReviewTabDisabled ? [] : [videoReviewTab]),
    ];

    let pitcherTabs = [];
    pitcherTabs.push({
        title: translate('sections.opponent.pitcher-overview'),
        route: 'pitcher-overview',
        component: ({ playerId }) => <PitcherOverview pitcherId={playerId} />,
    });
    if (!window.$MatchupPitcherGamePrepDisabled) {
        pitcherTabs.push({
            title: translate('sections.opponent.game-prep'),
            route: 'game-prep',
            component: ({ playerId }) => <GamePrep pitcherId={playerId} />,
        });
    }
    if (!window.$MatchupPitcherGameLogsDisabled) {
        pitcherTabs.push({
            title: translate('sections.opponent.game-logs'),
            route: 'game-logs',
            component: ({ playerId, player }) => <GameLogs pitcherId={playerId} pitcher={player} />,
        });
    }
    if (!window.$MatchupPitcherApproachDisabled) {
        pitcherTabs.push({
            title: translate('sections.opponent.approach'),
            route: 'approach',
            component: ({ isLoading, player, playerId }) => <Approach isLoadingPitcher={isLoading} pitcher={player} pitcherIdRaw={playerId} loggedInPlayerBats={Bats} />,
        });
    }
    if (opposingTeam.LevelID === 8) {
        pitcherTabs.push({
            title: translate('sections.opponent.tipping'),
            route: 'tipping',
            component: ({ isLoading, player, playerId }) => (
                <TippingReport isLoading={isLoading} pitcher={player} pitcherId={playerId}>
                    {translate('sections.opponent.tipping')}
                </TippingReport>
            ),
        });
    }
    if (!window.$MatchupPastMatchupTabDisabled) {
        pitcherTabs.push({
            title: translate('sections.opponent.past-matchups'),
            route: 'past-matchups',
            component: ({ isLoading, player }) => (
                <MatchupReportBase isPlayerLoading={isLoading} player={player} isPastMatchups isOpposingPitcherVersion>
                    {translate('sections.opponent.past-matchups')}
                </MatchupReportBase>
            ),
        });
    }
    pitcherTabs = [...pitcherTabs, ...playerTabs];

    let hitterTabs = [
        {
            title: translate('sections.opponent.matchup-report'),
            route: 'matchup',
            component: ({ isLoading, player, playerTeamStartingPitchers }) => (
                <MatchupReportBase
                    isPlayerLoading={isLoading}
                    player={player}
                    playerTeamStartingPitchers={playerTeamStartingPitchers}
                    isPastMatchups={false}
                >
                    {translate('sections.opponent.matchup')}
                </MatchupReportBase>
            ),
        },
        {
            title: translate('sections.opponent.key-takeaways'),
            route: 'keys',
            component: ({ isLoading, player, playerId, playerTeamStartingPitchers }) => (
                <KeyTakeaways
                    isPlayerLoading={isLoading}
                    player={player}
                    playerId={playerId}
                    playerTeamStartingPitchers={playerTeamStartingPitchers}
                >
                    {translate('sections.opponent.key-takeaways')}
                </KeyTakeaways>
            ),
        },
    ];
    if (!window.$MatchupPastMatchupTabDisabled) {
        hitterTabs.push({
            title: translate('sections.opponent.past-matchups'),
            route: 'past-matchups',
            component: ({ isLoading, player, playerTeamStartingPitchers }) => (
                <MatchupReportBase isPlayerLoading={isLoading} player={player} isPastMatchups playerTeamStartingPitchers={playerTeamStartingPitchers}>
                    {translate('sections.opponent.past-matchups')}
                </MatchupReportBase>
            ),
        });
    }
    hitterTabs.push({
        title: translate('sections.opponent.infield-positioning'),
        route: 'infield-positioning',
        component: ({ isLoading, player, playerTeamStartingPitchers }) => (
            <BaseInfieldReport
                isPlayerLoading={isLoading}
                player={player}
                playerTeamStartingPitchers={playerTeamStartingPitchers}
                opposingTeamID={teamId}
            />
        ),
    });
    hitterTabs = [...hitterTabs, ...playerTabs];

    const prefix = `${url}/`;
    return (
        <StyledInGameReport>
            <TeamSeriesHeader teamId={teamId} team={team} isDugout={window.$IsDugoutApp} />
            <StyledInGameReportContainer>
                <StyledInGameReportSelector>
                    {!!reports.length && (
                        <div>
                            <p>{translate('sections.opponent.reports')}</p>
                            {reports.map(({ title, route }) => (
                                <NavLink className="h5" key={route} to={`${prefix}${route}`}>{title}</NavLink>
                            ))}
                        </div>
                    )}
                    {(!IsPitcher || IsCatcher) && (
                        <div>
                            <p>{translate('baseball.pitcher_other')}</p>
                            <PlayerSelectorCards
                                teamId={teamId}
                                position="pitcher"
                                routePrefix={`${prefix}pitchers/`}
                                responsive={playerSelectorCardsResponsive}
                                hideHeadshots={window.$HidePlayerHeadshots}
                                tabs={pitcherTabs}
                            />
                        </div>
                    )}
                    {(IsPitcher || IsCatcher) && (
                        <div>
                            <p>{translate('baseball.hitter_other')}</p>
                            <PlayerSelectorCards
                                teamId={teamId}
                                position="batter"
                                routePrefix={`${prefix}batters/`}
                                responsive={playerSelectorCardsResponsive}
                                hideHeadshots={window.$HidePlayerHeadshots}
                                tabs={hitterTabs}
                            />
                        </div>
                    )}
                </StyledInGameReportSelector>
                <StyledInGameReportContent>
                    {!team && (<div><LoadingSpinner /></div>)}
                    {/* {team && IsCatcher && isLoadingStartingPitchers && (<div><LoadingSpinner /></div>)} */}
                    {team /* && (!IsCatcher || !isLoadingStartingPitchers) */ && (
                        <>
                            <Route path={path} exact>
                                <StyledLandingPage $numReports={reports.length}>
                                    <h4>← {translate('common.select-a-team-report')}</h4>
                                    <h4>← {translate('common.or')} {translate('common.select-a-player')}</h4>
                                </StyledLandingPage>
                            </Route>
                            {reports.map(({ route, render }) => (
                                <Route key={route} path={`${path}/${route}`} render={() => render({ team })} />
                            ))}
                            <Route
                                path={`${path}/batters/:batterId(\\d+)`}
                                render={(routeProps) => (
                                    <ErrorBoundary FallbackComponent={ComponentErrorFallback} resetKeys={[routeProps.match?.params?.batterId]}>
                                        <OpposingBatterReport {...routeProps} tabs={hitterTabs} />
                                    </ErrorBoundary>
                                )}
                            />
                            <Route
                                path={`${path}/pitchers/:pitcherId(\\d+)`}
                                render={(routeProps) => (
                                    <ErrorBoundary FallbackComponent={ComponentErrorFallback} resetKeys={[routeProps.match?.params?.pitcherId]}>
                                        <OpposingPitcherReport {...routeProps} team={team} tabs={pitcherTabs} />
                                    </ErrorBoundary>
                                )}
                            />
                        </>
                    )}
                </StyledInGameReportContent>
            </StyledInGameReportContainer>
        </StyledInGameReport>
    );
};

Opponent.propTypes = {
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string,
    }).isRequired,
    match: PropTypes.shape({
        path: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        params: PropTypes.shape({
            teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired,
    }).isRequired,
};

export default Opponent;
